import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/lib/api/auth";
import { getPostLoginRedirectPath } from "@/lib/authRedirect";
import { changePasswordSchema, type ChangePasswordValues } from "../schemas/authSchemas";
import { tempPasswordStore } from "@/lib/tempPasswordStore";
import { toast } from "@/lib/toast";
import { APIError } from "@/lib/api/client";
import { useI18n } from "@/context/I18nContext";

export function useChangePasswordForm() {
  const { user, logout, clearTemporaryPassword } = useAuth();
  const router = useRouter();
  const { t } = useI18n();

  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { new_password: "", confirm_new_password: "" },
    mode: "onBlur",
  });

  const mutation = useMutation({
    mutationFn: (values: ChangePasswordValues) => {
      const currentPassword = tempPasswordStore.get();
      if (!currentPassword) {
        // Session lost (e.g. page refresh) — force re-login
        logout();
        router.replace("/(auth)/login");
        return Promise.reject(new Error("session_expired"));
      }
      return authApi.changePassword({
        current_password: currentPassword,
        new_password: values.new_password,
      });
    },
    onSuccess: (_data, _vars, _ctx) => {
      tempPasswordStore.clear();
      clearTemporaryPassword();
      toast.success({ message: t("auth.changePassword.success") });
      if (user) {
        router.replace(getPostLoginRedirectPath(user.roles, user.vendorStatus));
      } else {
        router.replace("/(auth)/login");
      }
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync(values).catch(() => {});
  });

  const mapChangePasswordError = useCallback((err: unknown): string => {
    if (err instanceof APIError) {
      const code = err.errorCode?.toUpperCase();
      switch (code) {
        case "SAME_PASSWORD":
        case "NEW_PASSWORD_MUST_BE_DIFFERENT":
        case "PASSWORD_MUST_BE_DIFFERENT":
          return "auth.changePassword.error.samePassword";
        case "CURRENT_PASSWORD_INCORRECT":
        case "INVALID_CURRENT_PASSWORD":
        case "INCORRECT_CURRENT_PASSWORD":
          return "auth.changePassword.error.currentPasswordIncorrect";
        default:
          if (err.status === 400) {
            return "auth.changePassword.error.currentPasswordIncorrect";
          }
      }
    }
    return "common.error.generic";
  }, []);

  const rawError = mutation.error;
  const error = rawError ? mapChangePasswordError(rawError) : null;

  return {
    form,
    isLoading: mutation.isPending,
    error,
    handleSubmit,
  };
}
